import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCcw, Play, RotateCw, Archive, Copy, ShieldCheck } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { EnvStatusBadge, EnvTypeBadge } from "./EnvBadges";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}

function InfoRow({ label, value, testid, mono = false }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0 gap-3">
      <div className="text-[11px] text-white/50 uppercase tracking-wider shrink-0">{label}</div>
      <div className={`text-sm text-white font-semibold text-right truncate min-w-0 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}

function SnapshotPanel({ title, data = {}, testid }) {
  const entries = Object.entries(data || {});
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold mb-2">{title}</div>
      {entries.length === 0 ? (
        <div className="text-xs text-white/40 italic">Snapshot kosong.</div>
      ) : (
        <div className="space-y-1 max-h-56 overflow-auto pr-1">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-3 text-xs">
              <span className="font-mono text-white/50 truncate">{k}</span>
              <span className="font-mono text-white/80 text-right truncate max-w-[60%]">
                {typeof v === "boolean" ? (v ? "✓" : "✗") : (v ?? "—")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EnvironmentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [envContent, setEnvContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/platform/environments/${id}`),
        api.get(`/platform/environments/${id}/sanitized-env`),
      ]);
      setCfg(c.data);
      setEnvContent(s.data);
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb) => {
    setBusy(true);
    try {
      await api.post(`/platform/environments/${id}/${verb}`, {});
      toast.success(`Config ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };

  const copyEnv = async () => {
    if (!envContent?.content) return;
    try {
      await navigator.clipboard.writeText(envContent.content);
      toast.success("Sanitized ENV disalin ke clipboard.");
    } catch { toast.error("Gagal salin (clipboard tidak tersedia)."); }
  };

  if (loading || !cfg) {
    return (
      <PlatformLayout title="Environment Config">
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      </PlatformLayout>
    );
  }

  const canActivate = cfg.status === "GENERATED";
  const canArchive = ["GENERATED", "ACTIVE"].includes(cfg.status);

  return (
    <PlatformLayout title={`${cfg.app_name} · v${cfg.config_version}`}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/deployments/environments")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="env-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="env-refresh-btn">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          {canActivate && (
            <Button onClick={() => act("activate")} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="env-detail-activate">
              <Play size={14} className="mr-1" /> Activate
            </Button>
          )}
          <Button onClick={() => act("regenerate")} disabled={busy} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="env-detail-regenerate">
            <RotateCw size={14} className="mr-1" /> Regenerate
          </Button>
          {canArchive && (
            <Button onClick={() => act("archive")} disabled={busy} variant="outline" className="border-neutral-500/40 text-neutral-300 hover:bg-neutral-500/10" data-testid="env-detail-archive">
              <Archive size={14} className="mr-1" /> Archive
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="text-white font-display font-black text-lg" data-testid="env-detail-name">{cfg.app_name}</div>
            <EnvStatusBadge status={cfg.status} />
            <EnvTypeBadge environment={cfg.environment} />
            <span className="text-xs text-white/50 font-mono">v{cfg.config_version}</span>
          </div>

          <InfoRow label="Tenant" value={cfg.tenant_slug} testid="env-info-tenant" mono />
          <InfoRow label="Plan" value={cfg.plan_code} testid="env-info-plan" />
          <InfoRow label="Template" value={cfg.template_id || "—"} testid="env-info-template" mono />
          <InfoRow label="App URL" value={cfg.app_url} testid="env-info-app-url" mono />
          <InfoRow label="API URL" value={cfg.api_url} testid="env-info-api-url" mono />
          <InfoRow label="Domain" value={cfg.tenant_domain || "—"} testid="env-info-domain" mono />
          <InfoRow label="Database Name" value={cfg.database_name} testid="env-info-db" mono />
          <InfoRow label="Upload Path" value={cfg.upload_path} testid="env-info-upload" mono />
          <InfoRow label="Storage Path" value={cfg.storage_path} testid="env-info-storage" mono />
          <InfoRow label="Generated By" value={cfg.generated_by || "—"} testid="env-info-generated-by" />
          <InfoRow label="Generated At" value={fmt(cfg.generated_at)} testid="env-info-generated-at" />
          <InfoRow label="Updated At" value={fmt(cfg.updated_at)} testid="env-info-updated-at" />
        </div>

        <div className="space-y-4">
          <SnapshotPanel title="Branding Snapshot" data={cfg.branding_snapshot} testid="env-branding-snapshot" />
          <SnapshotPanel title="Feature Snapshot" data={cfg.feature_snapshot} testid="env-feature-snapshot" />
        </div>
      </div>

      {/* Sanitized ENV preview */}
      <div className="mt-5 rounded-2xl border border-white/10 bg-black/60 overflow-hidden" data-testid="env-sanitized-block">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/5">
          <ShieldCheck size={14} className="text-emerald-300" />
          <div className="text-[11px] uppercase tracking-widest text-white/70 font-black">Sanitized ENV (safe, no raw secrets)</div>
          <Button size="sm" variant="ghost" onClick={copyEnv} className="ml-auto text-white/60 hover:text-white hover:bg-white/10" data-testid="env-copy-btn">
            <Copy size={12} className="mr-1" /> Copy
          </Button>
        </div>
        <pre className="p-4 text-xs font-mono text-emerald-200 overflow-auto max-h-80 whitespace-pre-wrap break-words" data-testid="env-sanitized-content">
{envContent?.content || "—"}
        </pre>
        <div className="px-4 py-2 border-t border-white/10 bg-white/5 text-[10px] text-white/40">
          Secrets tampil sebagai reference (secret_xxx). Raw JWT_SECRET dan SECRET_KEY tidak pernah dikirim ke frontend.
        </div>
      </div>

      <div className="mt-4">
        <Link to="/platform/deployments/environments" className="text-xs text-white/50 hover:text-white underline">← Semua environment config</Link>
      </div>
    </PlatformLayout>
  );
}
